/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useEffect, useState } from "react";
import { FaPauseCircle, FaPlayCircle } from "react-icons/fa";
import ReactPlayer from "react-player";
import { useDispatch } from "react-redux";
import { HeadersFunction, LoaderFunction, MetaFunction, useLoaderData } from "react-router";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "~/components/ui/accordion";
import niyam from "~/data/nitya-niyam.json";
import { useFontStyle } from "~/hook/useFontStyle";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { addAudioQueue, addToCurrentAudio, changeIsAudioPlayState, changeIsPlayingState } from "~/lib/store/slice/audioSlice";
import { onPauseVideo } from "~/lib/store/slice/youtubePlayerSlice";
import { useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { AUDIO_PLAYER, CACHED_PAGE, FALLBACK_IMAGE_URL, LANG, PLAY_PAUSE_ICON_COLOR, youtubeLinkPrefix } from "~/utils/constant";
import { computeEtagFromData, homeRouteString } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

export const loader: LoaderFunction = async ({ params, request }) => {
  const lang = params.lang || "en";
  const apiService = new ApiService(lang || LANG.english);
  const response = await apiService.getNityaNiyam();

  const DATA = {
    title: lang === LANG.english ? "Nitya Niyam" : "નિત્ય નિયમ",
    nityaNiyamData: response || niyam,
  };

  const etag = computeEtagFromData(DATA);

  const ifNoneMatch = request.headers.get("If-None-Match");
  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: { ...CACHED_PAGE.NITYA_NIYAM.HEADERS, ETag: etag },
  });
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

export const meta: MetaFunction = ({ location, params }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";
  const meta = getPageMeta("nityaNiyam", lang);

  return metaService({
    title: meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: pathname,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
        ]}
      />
    );
  },
};

function NityaNiyam() {
  const dispatch = useDispatch();
  const { nityaNiyamData } = useLoaderData();
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
  const [selectedPlayItem, setSelectedPlayItem] = useState<number | null>(null);
  const [openAccordion, setOpenAccordion] = useState<string>(nityaNiyamData[0]?.name || "");

  const { isPlaying, currentAudio } = useAppSelector((state) => state.nityNiyam);
  const { isVideoPlay } = useAppSelector((state) => state.youtubeVideo);
  const { medium, normalInter } = useFontStyle();

  const handleOnAudioClick = async (id: number) => {
    const selectedNiyam = nityaNiyamData?.find((item) => item.id === id);
    if (!selectedNiyam) return;
    setSelectedPlayItem(id);

    // Only expand accordion if video ID is available
    if (selectedNiyam.youtube_id) {
      setOpenAccordion(selectedNiyam.name);
      setIsVideoPlaying(true);
      dispatch(changeIsPlayingState(false));
      dispatch(onPauseVideo());
      return;
    }

    const audioElement = new Audio(selectedNiyam.audioTrack);

    audioElement.addEventListener("loadedmetadata", () => {
      const totalDuration = audioElement.duration.toString();

      const currentAudio = {
        id: selectedNiyam.id,
        img: FALLBACK_IMAGE_URL,
        totalDuration, // 👈 set here
        artist: null,
        name: selectedNiyam.name,
        audio: selectedNiyam.audioTrack,
        from: "nityNiyam",
      };

      dispatch(addToCurrentAudio(currentAudio));
      sessionStorage.setItem(AUDIO_PLAYER.CURRENT_AUDIO, JSON.stringify(currentAudio));

      const audioQueue = nityaNiyamData?.map((item) => ({
        id: item.id,
        img: FALLBACK_IMAGE_URL,
        totalDuration: null, // optionally fetch duration for all later
        artist: null,
        name: item.name,
        audio: item.audioTrack,
        from: "nityNiyam",
      }));

      dispatch(addAudioQueue(audioQueue));
      sessionStorage.setItem(AUDIO_PLAYER.AUDIO_QUEUE, JSON.stringify(audioQueue));

      dispatch(changeIsPlayingState(true));
      sessionStorage.setItem(AUDIO_PLAYER.IS_PLAY, JSON.stringify(true));

      dispatch(changeIsAudioPlayState(true));
      sessionStorage.setItem(AUDIO_PLAYER.IS_AUDIO_PLAYING, "true");
      setIsVideoPlaying(false);
    });
  };

  const handleAccordionChange = (value: string) => {
    // Close video when accordion collapses
    if (!value) {
      setIsVideoPlaying(false);
    }
    setOpenAccordion(value);
  };

  useEffect(() => {
    if (isPlaying || isVideoPlay) {
      setIsVideoPlaying(false);
    }
  }, [isPlaying, isVideoPlay]);

  return (
    <div className="w-full flex flex-col justify-center items-center gap-2 px-3 mb-5 ">
      <Accordion
        type="single"
        collapsible
        value={openAccordion}
        onValueChange={handleAccordionChange}
        className="flex flex-col gap-4 justify-start items-center w-full md:w-[50%] cursor-pointer"
      >
        {nityaNiyamData?.map((item, index) => (
          <AccordionItem value={item.name} className="w-full bg-secondaryColor rounded-xl p-4 flex flex-col " key={item.id}>
            <AccordionTrigger className="w-full flex justify-between items-center" iconVisible={true}>
              <div className="w-full flex justify-start gap-2 items-center pr-2">
                {(isPlaying && currentAudio?.id === item.id) || (isVideoPlaying && selectedPlayItem === item.id) ? (
                  <div
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(changeIsPlayingState(false));
                      setIsVideoPlaying(false);
                      sessionStorage.setItem(AUDIO_PLAYER.IS_PLAY, JSON.stringify(false));
                    }}
                  >
                    <FaPauseCircle size={25} color={PLAY_PAUSE_ICON_COLOR} />
                  </div>
                ) : (
                  <div
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOnAudioClick(item.id);
                    }}
                  >
                    <FaPlayCircle size={25} color={PLAY_PAUSE_ICON_COLOR} />
                  </div>
                )}
                <h2 className={cn("body_text_m capitalize font-normal text-textColor", medium)}>{item.gu_name}</h2>
              </div>
            </AccordionTrigger>
            <AccordionContent className="w-full">
              <div className="w-full mt-3 border-t-2 border-t-borderPrimary py-4">
                {item.content ? (
                  <div className={cn("text-lg font-normal justify-start text-textColor", normalInter)}>
                    <div dangerouslySetInnerHTML={{ __html: item.content }} />
                  </div>
                ) : item?.youtube_id ? (
                  <div className="w-full aspect-video flex justify-center items-center">
                    <ReactPlayer
                      url={`${youtubeLinkPrefix}${item.youtube_id}`}
                      width="100%"
                      height="100%"
                      playing={isVideoPlaying}
                      controls
                      onPause={() => setIsVideoPlaying(false)}
                      onPlay={() => handleOnAudioClick(item.id)}
                    />
                  </div>
                ) : null}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

export default NityaNiyam;
